import { FrequencyRecord } from '~/interfaces';

/**
 * How far the pitch has to move to count as having moved at all.
 *
 * In cents rather than hertz. This used to compare `Math.round(frequency / 2)`, an absolute two-hertz
 * deadband - which is about thirty cents at a bass's A2 and about five at a soprano's E5, so the same
 * singing registered as vibrato at the top of the range and as a flat held note at the bottom. Ten
 * cents is roughly what the old test came to in the middle of the range, where it was tuned.
 */
const MIN_PITCH_CHANGE_CENTS = 10;

/** Swings examined together. Each one is half a cycle, so this is two and a half cycles of vibrato. */
const WINDOW_SIZE = 5;

/** How far the swings may vary from their own average and still count as regular. */
const REGULARITY = 1.75;

/**
 * How fast a regular oscillation has to be to be vibrato rather than something else.
 *
 * Sung vibrato sits at four to seven cycles a second; the band is opened either side of that so an
 * unhurried or a nervy singer still qualifies. Regularity alone does not separate vibrato from a
 * slow wobble in and out of tune, which is a fault rather than an ornament, or from the pitch
 * detector jittering at frame rate - and both used to be credited as vibrato and paid for.
 */
const MIN_RATE_HZ = 2.8;
const MAX_RATE_HZ = 9;

const MIN_HALF_CYCLE_MS = 1000 / (2 * MAX_RATE_HZ);
const MAX_HALF_CYCLE_MS = 1000 / (2 * MIN_RATE_HZ);

const centsBetween = (from: number, to: number) => (from > 0 && to > 0 ? Math.abs(1200 * Math.log2(to / from)) : 0);

export default function detectVibrato(data: FrequencyRecord[]): boolean {
  // When the pitch turned around, in order. The first reversal only marks where the swinging starts:
  // the gap before it is the time since the previous sample, not half a cycle, and counting it as
  // one dragged any window holding it below the regularity test - so vibrato that began at the top
  // of a note was rejected while the same vibrato later in the note passed.
  const reversals: number[] = [];
  let direction: '>' | '<' | null = null;

  for (let index = 1; index < data.length; index++) {
    if (centsBetween(data[index - 1].frequency, data[index].frequency) < MIN_PITCH_CHANGE_CENTS) continue;

    const next = data[index].frequency > data[index - 1].frequency ? '>' : '<';
    if (next === direction) continue;

    reversals.push(data[index].timestamp);
    direction = next;
  }

  // One half-cycle each, so the window slides over the quantity actually being tested for regularity
  // rather than over the turning points either side of it. `WINDOW_SIZE` swings need `WINDOW_SIZE`
  // entries here - the loop used to ask for one more than it went on to read.
  const halfCycles = reversals.slice(1).map((timestamp, index) => timestamp - reversals[index]);

  for (let index = 0; index + WINDOW_SIZE <= halfCycles.length; index++) {
    const window = halfCycles.slice(index, index + WINDOW_SIZE);
    const average = window.reduce((total, halfCycle) => total + halfCycle, 0) / window.length;

    if (average < MIN_HALF_CYCLE_MS || average > MAX_HALF_CYCLE_MS) continue;
    if (Math.max(...window) < average * REGULARITY && Math.min(...window) > average / REGULARITY) return true;
  }

  return false;
}
